import React, { useEffect, useState, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CheckCircleIcon, XIcon } from 'lucide-react';
type Purchase = {
  nombre: string;
  ciudad: string;
  minutos: number;
};
type ScheduledDisplay = {
  delay: number;
  id: number;
};
const purchases: Purchase[] = [{
  nombre: 'Marcos D.',
  ciudad: 'Buenos Aires',
  minutos: 12
}, {
  nombre: 'Valentina R.',
  ciudad: 'Córdoba',
  minutos: 7
}, {
  nombre: 'Diego F.',
  ciudad: 'Rosario',
  minutos: 21
}, {
  nombre: 'Camila S.',
  ciudad: 'Mendoza',
  minutos: 4
}, {
  nombre: 'Lucas P.',
  ciudad: 'La Plata',
  minutos: 15
}, {
  nombre: 'Julieta M.',
  ciudad: 'Mar del Plata',
  minutos: 9
}, {
  nombre: 'Federico A.',
  ciudad: 'Salta',
  minutos: 18
}];
const DISPLAY_DURATION_MS = 5000;
const CLOSE_COOLDOWN_MS = 20000;
export function SocialProofToast() {
  const reduceMotion = useReducedMotion();
  const lastIndexRef = useRef(-1);
  const [purchase, setPurchase] = useState(purchases[0]);
  const [isVisible, setIsVisible] = useState(false);
  const [schedule, setSchedule] = useState<ScheduledDisplay>(() => ({
    delay: getRandomDelay(),
    id: 0
  }));
  useEffect(() => {
    let hideTimer: number | undefined;
    const showTimer = window.setTimeout(() => {
      const nextIndex = getNextPurchaseIndex(lastIndexRef.current);
      lastIndexRef.current = nextIndex;
      setPurchase(withAdjustedMinutes(purchases[nextIndex]));
      setIsVisible(true);
      hideTimer = window.setTimeout(() => {
        setIsVisible(false);
        setSchedule(current => ({
          delay: getRandomDelay(),
          id: current.id + 1
        }));
      }, DISPLAY_DURATION_MS);
    }, schedule.delay);
    return () => {
      window.clearTimeout(showTimer);
      if (hideTimer) window.clearTimeout(hideTimer);
    };
  }, [schedule]);
  const handleClose = () => {
    setIsVisible(false);
    setSchedule(current => ({
      delay: CLOSE_COOLDOWN_MS,
      id: current.id + 1
    }));
  };
  const initials = getInitials(purchase.nombre);
  return <div className="pointer-events-none fixed bottom-6 left-4 z-[60] w-[calc(100vw-2rem)] max-w-sm sm:left-6" data-id="element-128">
      <AnimatePresence data-id="element-129">
        {isVisible ? <motion.aside className="pointer-events-auto relative flex items-center gap-3 border border-[#d4af37]/40 bg-card px-4 py-4 pr-10 shadow-[0_16px_34px_rgba(0,0,0,0.34)]" role="status" aria-live="polite" initial={reduceMotion ? false : {
        opacity: 0,
        x: -28
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        x: -18
      }} transition={{
        duration: reduceMotion ? 0 : 0.34,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-130">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-gold font-mono text-xs font-bold text-ink" aria-hidden="true" data-id="element-131">
              {initials}
            </div>
            <p className="text-sm leading-5 text-ivory" data-id="element-132">
              <span className="font-semibold" data-id="element-133">{purchase.nombre}</span> de{' '}
              {purchase.ciudad} compró el Megapack Entrenadores hace{' '}
              {purchase.minutos} minutos.
            </p>
            <CheckCircleIcon className="shrink-0 text-gold" size={19} strokeWidth={2} aria-label="Compra confirmada" data-id="element-134" />
            <button type="button" onClick={handleClose} className="absolute right-2 top-2 grid h-7 w-7 place-items-center text-sage transition-colors hover:text-ivory focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold" aria-label="Cerrar aviso de compra reciente" data-id="element-135">
              <XIcon size={16} strokeWidth={2} aria-hidden="true" data-id="element-136" />
            </button>
          </motion.aside> : null}
      </AnimatePresence>
    </div>;
}
function getRandomDelay() {
  return (18 + Math.floor(Math.random() * 8)) * 1000;
}
function getNextPurchaseIndex(previousIndex: number) {
  if (purchases.length < 2) return 0;
  let nextIndex = previousIndex;
  while (nextIndex === previousIndex) {
    nextIndex = Math.floor(Math.random() * purchases.length);
  }
  return nextIndex;
}
function withAdjustedMinutes(purchase: Purchase): Purchase {
  return {
    ...purchase,
    minutos: Math.max(1, purchase.minutos + Math.floor(Math.random() * 7) - 3)
  };
}
function getInitials(name: string) {
  return name.split(' ').map(part => part.replace(/[^A-Za-zÁÉÍÓÚÜÑáéíóúüñ]/g, '').charAt(0)).filter(Boolean).join('').slice(0, 2);
}